import { Component, EventEmitter, Input, Output, SimpleChanges, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { TranslationService } from 'src/app/Service/translation.service';
import { ASHRAM_LIST, CONSTANTS, RETREAT_LIST } from '../../../../Core/Constant';
import { environment } from 'src/environments/environment';

interface RetreatHistoryItem {
  CompletionPercentage: number;
  IsActive: boolean;
  Id: number;
  SevaProfileDisplayUID: string;
  RetreatName: string;
  RetreatDate: string;
  SaveMode: number;
}

@Component({
  selector: 'app-study-participation-information',
  templateUrl: './study-participation-information.component.html',
  styleUrls: ['./study-participation-information.component.scss']
})
export class StudyParticipationInformationComponent implements AfterViewInit {

  @Input() applicantData: any; // Accept data from parent component
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  retreatOptions = RETREAT_LIST;
  ashramOptions = ASHRAM_LIST;
  siddhaYogaParticipationForm!: FormGroup;
  submitted = false;
  SaveMode = CONSTANTS.SAVE_MODE
  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment
  selectedLanguage: string = 'en'; // Default language
  currentYear = new Date().getFullYear(); // Current year for maxYear restriction
  today = new Date(); // Today's date for maxDate restriction
  // Guard to suppress valueChanges-triggered API calls during initial patching
  private isBootstrapping: boolean = false;
  isEditable: boolean = true; // Flag to track if the form is editable

  // Center search properties - separate lists for each dropdown
  currentCenterList: { name: string, value: { Id: number, Name: string } }[] = [];
  otherCurrentCenterList: { name: string, value: { Id: number, Name: string } }[] = [];
  private currentCenterSearchTimeout: any;
  private otherCurrentCenterSearchTimeout: any;
  currentCenterSearchTerm: string = '';
  otherCurrentCenterSearchTerm: string = '';

  teachingOptions = [
    { name: 'Siddha Yoga Path Website', value: 'Siddha Yoga Path Website', translationKey: 'studyParticipation.siddhaYogaPathWebsite' },
    { name: 'Siddha Yoga Books', value: 'Siddha Yoga Books', translationKey: 'studyParticipation.siddhaYogaBooks' },
    { name: 'Home Study Course', value: 'Home Study Course', translationKey: 'studyParticipation.homeStudyCourse' },
    { name: 'Neeleshwari', value: 'Neeleshwari', translationKey: 'studyParticipation.neeleshwari' }
  ];

  practiceOptions = [
    { name: 'None', value: 'None', translationKey: 'studyParticipation.none' },
    { name: 'Meditation', value: 'Meditation', translationKey: 'studyParticipation.meditation' },
    { name: 'Chanting', value: 'Chanting', translationKey: 'studyParticipation.chanting' },
    { name: 'Yoga Asanas', value: 'Yoga Asanas', translationKey: 'studyParticipation.yogaAsanas' },
    { name: 'Study of Scriptures', value: 'Study of Scriptures', translationKey: 'studyParticipation.studyScriptures' },
    { name: 'Seva', value: 'Seva', translationKey: 'studyParticipation.seva' },
    { name: 'Other', value: 'Other', translationKey: 'studyParticipation.otherPractice' }
  ];



  constructor(
    private toastr: ToastrService, 
    private api: SevaProfileService, 
    private fb: FormBuilder, 
    public genericValidations: GenericValidationsService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Load saved language preference
    this.selectedLanguage = this.translationService.getCurrentLanguage();
    
    if (!this.isPreviewMode) {
      // Execute only when not in preview mode
      this.initializeForm();
      this.getApplicantData();
    }
  }

  // Get translation for current language
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }

  // Debug method to check CurrentCenterDetails binding state
  debugCurrentCenterDetailsBinding() {
    console.log('🔴 [DEBUG] ========== CurrentCenterDetails Binding Debug ==========');
    const control = this.siddhaYogaParticipationForm?.get('CurrentCenterDetails');
    const controlValue = control?.value;
    console.log('🔴 [DEBUG] Form control exists:', !!control);
    console.log('🔴 [DEBUG] Form control value:', JSON.stringify(controlValue, null, 2));
    console.log('🔴 [DEBUG] Form control value type:', typeof controlValue);
    console.log('🔴 [DEBUG] currentCenterList:', JSON.stringify(this.currentCenterList, null, 2));
    console.log('🔴 [DEBUG] currentCenterList length:', this.currentCenterList.length);
    
    if (controlValue && this.currentCenterList.length > 0) {
      console.log('🔴 [DEBUG] Attempting to find matching item...');
      this.currentCenterList.forEach((item, index) => {
        const match = this.compareCenters(controlValue, item.value);
        console.log(`🔴 [DEBUG] Item ${index}:`, {
          item: JSON.stringify(item, null, 2),
          itemValue: JSON.stringify(item.value, null, 2),
          controlValue: JSON.stringify(controlValue, null, 2),
          match
        });
      });
      const found = this.currentCenterList.find(item => this.compareCenters(controlValue, item.value));
      console.log('🔴 [DEBUG] Found matching item:', found ? JSON.stringify(found, null, 2) : 'NOT FOUND');
    } else {
      console.log('🔴 [DEBUG] Cannot find match - controlValue or list is empty');
    }
    console.log('🔴 [DEBUG] ======================================================');
  }

  ngAfterViewInit(): void {
    // Debug after view is initialized
    setTimeout(() => {
      console.log('🔴 [DEBUG] ngAfterViewInit - checking CurrentCenterDetails binding');
      this.debugCurrentCenterDetailsBinding();
    }, 200);
  }

  // Compare function for ng-select to properly match center objects
  compareCenters(c1: any, c2: any): boolean {
    console.log('🔍 [DEBUG] compareCenters called with:');
    console.log('  c1:', JSON.stringify(c1, null, 2));
    console.log('  c2:', JSON.stringify(c2, null, 2));
    console.log('  c1 type:', typeof c1, 'c1 keys:', c1 ? Object.keys(c1) : 'null');
    console.log('  c2 type:', typeof c2, 'c2 keys:', c2 ? Object.keys(c2) : 'null');
    
    if (!c1 || !c2) {
      const result = c1 === c2;
      console.log('🔍 [DEBUG] compareCenters result (null check):', result);
      return result;
    }
    
    // Handle different formats:
    // 1. If c1/c2 is the full item object {name: "...", value: {Id, Name}}, extract value.Id
    // 2. If c1/c2 is the value object {Id, Name}, use Id directly
    // 3. If c1/c2 is a number, use it directly
    
    let id1: number | undefined;
    if (typeof c1 === 'object') {
      // Check if it's the full item object with value property
      if (c1.value && typeof c1.value === 'object' && c1.value.Id) {
        id1 = c1.value.Id;
        console.log('🔍 [DEBUG] c1 is full item object, extracted id1 from value.Id:', id1);
      } else if (c1.Id) {
        // It's the value object directly
        id1 = c1.Id;
        console.log('🔍 [DEBUG] c1 is value object, extracted id1 from Id:', id1);
      } else {
        console.log('🔍 [DEBUG] c1 is object but no Id found, keys:', Object.keys(c1));
      }
    } else if (typeof c1 === 'number') {
      id1 = c1;
      console.log('🔍 [DEBUG] c1 is number:', id1);
    }
    
    let id2: number | undefined;
    if (typeof c2 === 'object') {
      // Check if it's the full item object with value property
      if (c2.value && typeof c2.value === 'object' && c2.value.Id) {
        id2 = c2.value.Id;
        console.log('🔍 [DEBUG] c2 is full item object, extracted id2 from value.Id:', id2);
      } else if (c2.Id) {
        // It's the value object directly
        id2 = c2.Id;
        console.log('🔍 [DEBUG] c2 is value object, extracted id2 from Id:', id2);
      } else {
        console.log('🔍 [DEBUG] c2 is object but no Id found, keys:', Object.keys(c2));
      }
    } else if (typeof c2 === 'number') {
      id2 = c2;
      console.log('🔍 [DEBUG] c2 is number:', id2);
    }
    
    const result = id1 !== undefined && id2 !== undefined && id1 === id2;
    console.log('🔍 [DEBUG] compareCenters final result:', { id1, id2, result });
    return result;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData) {
      this.initializeForm();
      this.isBootstrapping = true;

      // 👇 Step 1: Convert JSON string fields to arrays
      const arrayFields = [
        'IntensiveHistory',
        'AudioSatsangHistory',
        'LiveStreamHistory',
        'MDSHistory',
        'SweetSurpriseHistory',
        'EngagedTeachingDetails',
        'CurrentSYPractices'
      ];

      arrayFields.forEach(field => {
        if (this.applicantData[field] && typeof this.applicantData[field] === 'string') {
          try {
            this.applicantData[field] = JSON.parse(this.applicantData[field]);
          } catch (e) {
            // For CurrentSYPractices, if it's a single string value, convert it to an array
            if (field === 'CurrentSYPractices' && this.applicantData[field]) {
              this.applicantData[field] = [this.applicantData[field]];
            } else {
              console.warn(`Failed to parse ${field}:`, this.applicantData[field]);
              this.applicantData[field] = [];
            }
          }
        }
      });

      // 👇 Step 2: Convert year fields to numbers
      const yearFields = ['BeginPracticeYear', 'FirstIntensiveYear'];
      yearFields.forEach(field => {
        if (this.applicantData[field]) {
          this.applicantData[field] = Number(this.applicantData[field]);
        }
      });

      // 👇 Step 3: Handle CurrentCenterDetailsObj - convert to ng-select format
      let currentCenterObj: { Id: number, Name: string } | null = null;
      if (this.applicantData.CurrentCenterDetailsObj && typeof this.applicantData.CurrentCenterDetailsObj === 'object' && this.applicantData.CurrentCenterDetailsObj.Id) {
        currentCenterObj = { 
          Id: this.applicantData.CurrentCenterDetailsObj.Id, 
          Name: this.applicantData.CurrentCenterDetailsObj.Name || '' 
        };
        // Add to the center list first so ng-select can display it
        this.addCenterToList(currentCenterObj, 'current');
        // Don't set in applicantData - we'll set it directly on form control after patch
        this.applicantData.CurrentCenterDetails = null;
      } else {
        this.applicantData.CurrentCenterDetails = null;
      }

      // 👇 Step 4: Handle OtherCurrentCenterDetailsObj - convert to ng-select format
      // Only bind if PracticeBeginDetails matches the expected value
      let otherCenterObj: { Id: number, Name: string } | null = null;
      
      // Clear OtherCurrentCenterDetails if PracticeBeginDetails doesn't match
      // Note: CenterVisitFrequency is independent and should not be cleared
      if (this.applicantData.PracticeBeginDetails !== 'siddha-yoga-centre' && this.applicantData.PracticeBeginDetails !== 'other') {
        this.applicantData.OtherCurrentCenterDetails = null;
      } else if (this.applicantData.PracticeBeginDetails === 'siddha-yoga-centre' && 
          this.applicantData.OtherCurrentCenterDetailsObj && 
          typeof this.applicantData.OtherCurrentCenterDetailsObj === 'object' && 
          this.applicantData.OtherCurrentCenterDetailsObj.Id) {
        // Only bind if PracticeBeginDetails is 'siddha-yoga-centre'
        otherCenterObj = { 
          Id: this.applicantData.OtherCurrentCenterDetailsObj.Id, 
          Name: this.applicantData.OtherCurrentCenterDetailsObj.Name || '' 
        };
        // Add to the center list first so ng-select can display it
        this.addCenterToList(otherCenterObj, 'other');
        // Don't set in applicantData - we'll set it directly on form control after patch
        this.applicantData.OtherCurrentCenterDetails = null;
      } else if (this.applicantData.PracticeBeginDetails === 'other' && this.applicantData.OtherPracticeBeginDetails) {
        // If 'other' is selected, use the text value
        this.applicantData.OtherCurrentCenterDetails = this.applicantData.OtherPracticeBeginDetails;
      } else {
        // Clear if conditions don't match
        this.applicantData.OtherCurrentCenterDetails = null;
      }

      // 👇 Step 5: Map WebSiteSearchFrequency to SiddhaYogaPathWebsiteFrequency for form
      if (this.applicantData.WebSiteSearchFrequency !== undefined) {
        this.applicantData.SiddhaYogaPathWebsiteFrequency = this.applicantData.WebSiteSearchFrequency;
      }

      // 👇 Step 6: Patch form with all transformed data
      this.siddhaYogaParticipationForm.patchValue(this.applicantData);

      // 👇 Step 7: Set center dropdown values directly on form controls AFTER patch to ensure proper binding
      // Use setTimeout with a small delay to ensure Angular change detection has processed the list updates
      setTimeout(() => {
        if (currentCenterObj) {
          const currentCenterControl = this.siddhaYogaParticipationForm.get('CurrentCenterDetails');
          if (currentCenterControl && this.currentCenterList.length > 0) {
            // Ensure the center object matches the format in the list (value property)
            const matchingItem = this.currentCenterList.find(item => item.value.Id === currentCenterObj!.Id);
            if (matchingItem) {
              currentCenterControl.setValue(matchingItem.value, { emitEvent: false });
            } else {
              // Fallback: set the value directly
              currentCenterControl.setValue(currentCenterObj, { emitEvent: false });
            }
          }
        }

        if (otherCenterObj) {
          const otherCenterControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');
          if (otherCenterControl && this.otherCurrentCenterList.length > 0) {
            // Ensure the center object matches the format in the list (value property)
            const matchingItem = this.otherCurrentCenterList.find(item => item.value.Id === otherCenterObj!.Id);
            if (matchingItem) {
              otherCenterControl.setValue(matchingItem.value, { emitEvent: false });
            } else {
              // Fallback: set the value directly
              otherCenterControl.setValue(otherCenterObj, { emitEvent: false });
            }
          }
        }

        // Explicitly set CenterVisitFrequency to ensure it binds correctly
        if (this.applicantData.CenterVisitFrequency) {
          const centerVisitFrequencyControl = this.siddhaYogaParticipationForm.get('CenterVisitFrequency');
          if (centerVisitFrequencyControl) {
            centerVisitFrequencyControl.setValue(this.applicantData.CenterVisitFrequency, { emitEvent: false });
          }
        }
      }, 100);

      // Ensure conditional validation is set up after patching
      this.updateConditionalValidation();

      this.siddhaYogaParticipationForm.disable();
      setTimeout(() => { this.isBootstrapping = false; }, 0);
    }
  }









  


  getApplicantData() {

    this.api.apiCall('CurrentParticipation/GetCurrentParticipation', 'GET').subscribe((response: any) => {
     
     
      if (response.Data && Object.keys(response.Data).length > 0) {
        this.isBootstrapping = true;

        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage);

        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.siddhaYogaParticipationForm.disable();
        } else {
          this.siddhaYogaParticipationForm.enable();
        }

        // 👇 Step 1: Convert JSON string fields to arrays
        const arrayFields = [
          'IntensiveHistory',
          'AudioSatsangHistory',
          'LiveStreamHistory',
          'MDSHistory',
          'SweetSurpriseHistory',
          'EngagedTeachingDetails',
          'CurrentSYPractices'
        ];

        arrayFields.forEach(field => {
          if (response.Data[field] && typeof response.Data[field] === 'string') {
            try {
              response.Data[field] = JSON.parse(response.Data[field]);
            } catch (e) {
              // For CurrentSYPractices, if it's a single string value, convert it to an array
              if (field === 'CurrentSYPractices' && response.Data[field]) {
                response.Data[field] = [response.Data[field]];
              } else {
                console.warn(`Failed to parse ${field}:`, response.Data[field]);
                response.Data[field] = [];
              }
            }
          }
        });

        // 👇 Step 2: Convert year fields to numbers
        const yearFields = ['BeginPracticeYear', 'FirstIntensiveYear'];
        yearFields.forEach(field => {
          if (response.Data[field]) {
            response.Data[field] = Number(response.Data[field]);
          }
        });

        // 👇 Step 3: Handle CurrentCenterDetailsObj - convert to ng-select format
        let currentCenterObj: { Id: number, Name: string } | null = null;
        if (response.Data.CurrentCenterDetailsObj && typeof response.Data.CurrentCenterDetailsObj === 'object' && response.Data.CurrentCenterDetailsObj.Id) {
          currentCenterObj = { 
            Id: response.Data.CurrentCenterDetailsObj.Id, 
            Name: response.Data.CurrentCenterDetailsObj.Name || '' 
          };
          console.log('🔵 [DEBUG] CurrentCenterDetailsObj from API:', response.Data.CurrentCenterDetailsObj);
          console.log('🔵 [DEBUG] currentCenterObj created:', currentCenterObj);
          // Add to the center list first so ng-select can display it
          this.addCenterToList(currentCenterObj, 'current');
          console.log('🔵 [DEBUG] currentCenterList after addCenterToList:', this.currentCenterList);
          // Don't set in response.Data - we'll set it directly on form control after patch
          response.Data.CurrentCenterDetails = null;
        } else {
          response.Data.CurrentCenterDetails = null;
        }

        // 👇 Step 4: Handle OtherCurrentCenterDetailsObj - convert to ng-select format
        // Only bind if PracticeBeginDetails matches the expected value
        let otherCenterObj: { Id: number, Name: string } | null = null;
        console.log('🟢 [DEBUG] PracticeBeginDetails:', response.Data.PracticeBeginDetails);
        console.log('🟢 [DEBUG] OtherCurrentCenterDetailsObj from API:', response.Data.OtherCurrentCenterDetailsObj);
        
        // Clear OtherCurrentCenterDetails if PracticeBeginDetails doesn't match
        // Note: CenterVisitFrequency is independent and should not be cleared
        if (response.Data.PracticeBeginDetails !== 'siddha-yoga-centre' && response.Data.PracticeBeginDetails !== 'other') {
          response.Data.OtherCurrentCenterDetails = null;
          console.log('🟢 [DEBUG] PracticeBeginDetails is not siddha-yoga-centre or other, clearing OtherCurrentCenterDetails');
        } else if (response.Data.PracticeBeginDetails === 'siddha-yoga-centre' && 
            response.Data.OtherCurrentCenterDetailsObj && 
            typeof response.Data.OtherCurrentCenterDetailsObj === 'object' && 
            response.Data.OtherCurrentCenterDetailsObj.Id) {
          // Only bind if PracticeBeginDetails is 'siddha-yoga-centre'
          otherCenterObj = { 
            Id: response.Data.OtherCurrentCenterDetailsObj.Id, 
            Name: response.Data.OtherCurrentCenterDetailsObj.Name || '' 
          };
          console.log('🟢 [DEBUG] otherCenterObj created:', otherCenterObj);
          // Add to the center list first so ng-select can display it
          this.addCenterToList(otherCenterObj, 'other');
          console.log('🟢 [DEBUG] otherCurrentCenterList after addCenterToList:', this.otherCurrentCenterList);
          // Don't set in response.Data - we'll set it directly on form control after patch
          response.Data.OtherCurrentCenterDetails = null;
        } else if (response.Data.PracticeBeginDetails === 'other' && response.Data.OtherPracticeBeginDetails) {
          // If 'other' is selected, use the text value
          response.Data.OtherCurrentCenterDetails = response.Data.OtherPracticeBeginDetails;
        } else {
          // Clear if conditions don't match
          response.Data.OtherCurrentCenterDetails = null;
          console.log('🟢 [DEBUG] Clearing OtherCurrentCenterDetails - conditions not met');
        }

        // 👇 Step 5: Map WebSiteSearchFrequency to SiddhaYogaPathWebsiteFrequency for form
        if (response.Data.WebSiteSearchFrequency !== undefined) {
          response.Data.SiddhaYogaPathWebsiteFrequency = response.Data.WebSiteSearchFrequency;
        }

        // 👇 Step 6: Patch form with all transformed data (single patch operation)
        console.log('🟣 [DEBUG] Patching form with response.Data:', response.Data);
        console.log('🟣 [DEBUG] CenterVisitFrequency from API:', response.Data.CenterVisitFrequency);
        this.siddhaYogaParticipationForm.patchValue(response.Data);
        console.log('🟣 [DEBUG] Form after patchValue - CurrentCenterDetails:', this.siddhaYogaParticipationForm.get('CurrentCenterDetails')?.value);
        console.log('🟣 [DEBUG] Form after patchValue - OtherCurrentCenterDetails:', this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails')?.value);
        console.log('🟣 [DEBUG] Form after patchValue - CenterVisitFrequency:', this.siddhaYogaParticipationForm.get('CenterVisitFrequency')?.value);
        console.log('🟣 [DEBUG] currentCenterList at patch time:', this.currentCenterList);
        console.log('🟣 [DEBUG] otherCurrentCenterList at patch time:', this.otherCurrentCenterList);

        // 👇 Step 7: Set center dropdown values directly on form controls AFTER patch to ensure proper binding
        // Use setTimeout with a small delay to ensure Angular change detection has processed the list updates
        setTimeout(() => {
        console.log('🟡 [DEBUG] Setting form control values - currentCenterList:', JSON.stringify(this.currentCenterList, null, 2));
        console.log('🟡 [DEBUG] Setting form control values - otherCurrentCenterList:', JSON.stringify(this.otherCurrentCenterList, null, 2));
          
          if (currentCenterObj) {
            const currentCenterControl = this.siddhaYogaParticipationForm.get('CurrentCenterDetails');
            console.log('🔵 [DEBUG] currentCenterControl exists:', !!currentCenterControl);
            console.log('🔵 [DEBUG] currentCenterList.length:', this.currentCenterList.length);
            if (currentCenterControl && this.currentCenterList.length > 0) {
              // Ensure the center object matches the format in the list (value property)
              const matchingItem = this.currentCenterList.find(item => item.value.Id === currentCenterObj!.Id);
              console.log('🔵 [DEBUG] matchingItem found:', JSON.stringify(matchingItem, null, 2));
              console.log('🔵 [DEBUG] matchingItem.value:', JSON.stringify(matchingItem?.value, null, 2));
              if (matchingItem) {
                console.log('🔵 [DEBUG] Setting CurrentCenterDetails to:', JSON.stringify(matchingItem.value, null, 2));
                // Reassign list to trigger ng-select change detection
                this.currentCenterList = [...this.currentCenterList];
                // Use the exact object reference from the list to ensure ng-select can match it
                currentCenterControl.setValue(matchingItem.value, { emitEvent: false });
                console.log('🔵 [DEBUG] CurrentCenterDetails value after setValue:', JSON.stringify(currentCenterControl.value, null, 2));
                // Force change detection to update ng-select
                this.cdr.detectChanges();
                // Debug the binding state
                setTimeout(() => {
                  this.debugCurrentCenterDetailsBinding();
                  // Try setting again after a short delay to ensure ng-select has processed the list
                  if (currentCenterControl.value && this.currentCenterList.length > 0) {
                    const reMatch = this.currentCenterList.find(item => item.value.Id === currentCenterControl.value.Id);
                    if (reMatch) {
                      console.log('🔵 [DEBUG] Re-setting CurrentCenterDetails with exact match:', JSON.stringify(reMatch.value, null, 2));
                      // Reassign list again to trigger change detection
                      this.currentCenterList = [...this.currentCenterList];
                      currentCenterControl.setValue(reMatch.value, { emitEvent: false });
                      this.cdr.detectChanges();
                    }
                  }
                }, 100);
              } else {
                // Fallback: set the value directly
                console.log('🔵 [DEBUG] No matching item found, setting directly:', currentCenterObj);
                currentCenterControl.setValue(currentCenterObj, { emitEvent: false });
                console.log('🔵 [DEBUG] CurrentCenterDetails value after setValue (fallback):', currentCenterControl.value);
              }
            } else {
              console.log('🔵 [DEBUG] Cannot set CurrentCenterDetails - control or list issue');
            }
          }

          if (otherCenterObj) {
            const otherCenterControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');
            console.log('🟢 [DEBUG] otherCenterControl exists:', !!otherCenterControl);
            console.log('🟢 [DEBUG] otherCurrentCenterList.length:', this.otherCurrentCenterList.length);
            if (otherCenterControl && this.otherCurrentCenterList.length > 0) {
              // Ensure the center object matches the format in the list (value property)
              const matchingItem = this.otherCurrentCenterList.find(item => item.value.Id === otherCenterObj!.Id);
              console.log('🟢 [DEBUG] matchingItem found:', JSON.stringify(matchingItem, null, 2));
              console.log('🟢 [DEBUG] matchingItem.value:', JSON.stringify(matchingItem?.value, null, 2));
              if (matchingItem) {
                console.log('🟢 [DEBUG] Setting OtherCurrentCenterDetails to:', JSON.stringify(matchingItem.value, null, 2));
                // Reassign list to trigger ng-select change detection
                this.otherCurrentCenterList = [...this.otherCurrentCenterList];
                // Use the exact object reference from the list to ensure ng-select can match it
                otherCenterControl.setValue(matchingItem.value, { emitEvent: false });
                console.log('🟢 [DEBUG] OtherCurrentCenterDetails value after setValue:', JSON.stringify(otherCenterControl.value, null, 2));
                // Force change detection to update ng-select
                this.cdr.detectChanges();
                // Try setting again after a short delay to ensure ng-select has processed the list
                setTimeout(() => {
                  if (otherCenterControl.value && this.otherCurrentCenterList.length > 0) {
                    const reMatch = this.otherCurrentCenterList.find(item => item.value.Id === otherCenterControl.value.Id);
                    if (reMatch) {
                      console.log('🟢 [DEBUG] Re-setting OtherCurrentCenterDetails with exact match:', JSON.stringify(reMatch.value, null, 2));
                      // Reassign list again to trigger change detection
                      this.otherCurrentCenterList = [...this.otherCurrentCenterList];
                      otherCenterControl.setValue(reMatch.value, { emitEvent: false });
                      this.cdr.detectChanges();
                    }
                  }
                }, 100);
              } else {
                // Fallback: set the value directly
                console.log('🟢 [DEBUG] No matching item found, setting directly:', otherCenterObj);
                otherCenterControl.setValue(otherCenterObj, { emitEvent: false });
                console.log('🟢 [DEBUG] OtherCurrentCenterDetails value after setValue (fallback):', otherCenterControl.value);
              }
            } else {
              console.log('🟢 [DEBUG] Cannot set OtherCurrentCenterDetails - control or list issue');
            }
          }

          // Explicitly set CenterVisitFrequency to ensure it binds correctly
          if (response.Data.CenterVisitFrequency) {
            const centerVisitFrequencyControl = this.siddhaYogaParticipationForm.get('CenterVisitFrequency');
            if (centerVisitFrequencyControl) {
              console.log('🟡 [DEBUG] Setting CenterVisitFrequency to:', response.Data.CenterVisitFrequency);
              centerVisitFrequencyControl.setValue(response.Data.CenterVisitFrequency, { emitEvent: false });
              console.log('🟡 [DEBUG] CenterVisitFrequency after setValue:', centerVisitFrequencyControl.value);
            }
          }
        }, 100);

        // Populate Retreat History if available
        if (response.Data.RetreatHistory && response.Data.RetreatHistory.length > 0) {
          this.populateRetreatHistoryArray(response.Data.RetreatHistory);
        }

        // Populate Seva History if available
        if (response.Data.SevaHistory && response.Data.SevaHistory.length > 0) {
          this.populateSevaHistoryArray(response.Data.SevaHistory);
        }

        // Ensure conditional validation is set up after loading data
        this.updateConditionalValidation();
        // End bootstrap phase; defer to ensure subscribers attached have settled
        setTimeout(() => { this.isBootstrapping = false; }, 0);
      }
    })
  }

  initializeForm() {
    this.siddhaYogaParticipationForm = this.fb.group({
      BeginPracticeYear: [null, Validators.required],
      PracticeBeginDetails: ['', Validators.required],
      OtherCurrentCenterDetails: [null], // Changed to null for object binding
      CenterVisitFrequency: ['', Validators.required],

      CurrentSYPractices: [[], Validators.required],
      IsEngagedWithTeachings: [null, Validators.required],
      EngagedTeachingDetails: [[]],
      SiddhaYogaPathWebsiteFrequency: [''],

      IsParticipateInSadhanaCircle: [null, Validators.required],
      SadhanaCircleParticipationFrequency: [''],
      SaveMode: ['', [Validators.required,]],

      // New fields for event history
      FirstIntensiveYear: [null, Validators.required],
      IntensiveHistory: [[]],
      AudioSatsangHistory: [[]],
      LiveStreamHistory: [[]],
      MDSHistory: [[]],
      SweetSurpriseHistory: [[]],

      // Center dropdown - initialized as null for object binding
      CurrentCenterDetails: [null, Validators.required],

      // Add FormArrays for multi-add functionality
      retreatHistory: this.fb.array([]),
      sevaHistory: this.fb.array([])
    });

    // Set up conditional validation for OtherCurrentCenterDetails
    this.setupConditionalValidation();

    // Set up CurrentSYPractices change listener for mutual exclusivity
    this.setupCurrentSYPracticesListener();

    // Set up listeners for event history fields to handle "None" option
    this.setupEventHistoryListeners();

    const controlMappings = {
      // 'IsVisitingSiddhaYogaCentre': [ 'CenterVisitFrequency'],
      'PracticeBeginDetails': ['OtherCurrentCenterDetails'],
    };

    // Call the service with the isShowOn parameter set to true or false as needed
    this.genericValidations.setupFormListeners(this.siddhaYogaParticipationForm, controlMappings, true);


    // Add initial rows for Retreat History and Seva History
    this.addRetreatHistoryRow();
    this.addSevaHistoryRow();

    // Emit the form group to parent component
    this.formGroupChange.emit(this.siddhaYogaParticipationForm);


  }

  // Method to handle conditional validation for OtherCurrentCenterDetails
  setupConditionalValidation() {
    const practiceBeginDetailsControl = this.siddhaYogaParticipationForm.get('PracticeBeginDetails');
    const otherCurrentCenterDetailsControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');
    const centerVisitFrequencyControl = this.siddhaYogaParticipationForm.get('CenterVisitFrequency');

    if (practiceBeginDetailsControl) {
      practiceBeginDetailsControl.valueChanges.subscribe((value) => {
        // Skip if this is during bootstrap/initial load
        if (this.isBootstrapping) {
          return;
        }

        console.log('🟡 [DEBUG] PracticeBeginDetails changed to:', value);
        
        // Clear dependent fields when PracticeBeginDetails changes
        if (otherCurrentCenterDetailsControl) {
          otherCurrentCenterDetailsControl.setValue(null, { emitEvent: false });
          // Clear the other center list as well
          this.otherCurrentCenterList = [];
          this.otherCurrentCenterSearchTerm = '';
          console.log('🟡 [DEBUG] Cleared OtherCurrentCenterDetails and list');
        }

        if (centerVisitFrequencyControl) {
          centerVisitFrequencyControl.setValue(null, { emitEvent: false });
          console.log('🟡 [DEBUG] Cleared CenterVisitFrequency');
        }
        
        // Update validation for OtherCurrentCenterDetails based on new value
        if (otherCurrentCenterDetailsControl) {
          if (value === 'other' || value === 'siddha-yoga-centre') {
            // Add required validation when 'other' or 'siddha-yoga-centre' is selected
            otherCurrentCenterDetailsControl.setValidators([Validators.required]);
            otherCurrentCenterDetailsControl.markAsTouched();
          } else {
            // Remove validation when neither 'other' nor 'siddha-yoga-centre' is selected
            otherCurrentCenterDetailsControl.clearValidators();
            otherCurrentCenterDetailsControl.markAsPristine();
            otherCurrentCenterDetailsControl.markAsUntouched();
          }
          otherCurrentCenterDetailsControl.updateValueAndValidity();
        }

        // Force change detection
        this.cdr.detectChanges();
      });
    }
  }

  // Method to update conditional validation when data is loaded
  updateConditionalValidation() {
    const practiceBeginDetailsControl = this.siddhaYogaParticipationForm.get('PracticeBeginDetails');
    const otherCurrentCenterDetailsControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');

    if (practiceBeginDetailsControl && otherCurrentCenterDetailsControl) {
      const currentValue = practiceBeginDetailsControl.value;
      if (currentValue === 'other' || currentValue === 'siddha-yoga-centre') {
        // Add required validation when 'other' or 'siddha-yoga-centre' is selected
        otherCurrentCenterDetailsControl.setValidators([Validators.required]);
      } else {
        // Remove validation when neither 'other' nor 'siddha-yoga-centre' is selected
        otherCurrentCenterDetailsControl.clearValidators();
        otherCurrentCenterDetailsControl.setValue(null);
        otherCurrentCenterDetailsControl.markAsPristine();
        otherCurrentCenterDetailsControl.markAsUntouched();
      }
      otherCurrentCenterDetailsControl.updateValueAndValidity();
    }
  }


  // Helper method to check if OtherCurrentCenterDetails text input should be shown (for "other")
  isOtherCurrentCenterDetailsRequired(): boolean {
    const practiceBeginDetailsControl = this.siddhaYogaParticipationForm.get('PracticeBeginDetails');
    return practiceBeginDetailsControl?.value === 'other' || false;
  }

  // Helper method to check if OtherCurrentCenterDetails dropdown should be shown (for "siddha-yoga-centre")
  isOtherCurrentCenterDropdownRequired(): boolean {
    const practiceBeginDetailsControl = this.siddhaYogaParticipationForm.get('PracticeBeginDetails');
    return practiceBeginDetailsControl?.value === 'siddha-yoga-centre' || false;
  }

  // Helper method to check if OtherCurrentCenterDetails field is invalid
  isOtherCurrentCenterDetailsInvalid(): boolean {
    const otherCurrentCenterDetailsControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');
    return (otherCurrentCenterDetailsControl?.invalid && otherCurrentCenterDetailsControl?.touched) || false;
  }

  // Method to validate conditional fields
  validateConditionalFields(): boolean {
    const practiceBeginDetailsControl = this.siddhaYogaParticipationForm.get('PracticeBeginDetails');
    const otherCurrentCenterDetailsControl = this.siddhaYogaParticipationForm.get('OtherCurrentCenterDetails');

    if (practiceBeginDetailsControl?.value === 'other' || practiceBeginDetailsControl?.value === 'siddha-yoga-centre') {
      const value = otherCurrentCenterDetailsControl?.value;
      let isEmpty = false;

      if (practiceBeginDetailsControl?.value === 'other') {
        // For "other", value should be a string
        isEmpty = !value || (typeof value === 'string' && value.trim() === '');
      } else if (practiceBeginDetailsControl?.value === 'siddha-yoga-centre') {
        // For "siddha-yoga-centre", value should be an object with Id
        isEmpty = !value || (typeof value === 'object' && !value.Id);
      }

      if (isEmpty) {
        otherCurrentCenterDetailsControl?.markAsTouched();
        otherCurrentCenterDetailsControl?.setErrors({ required: true });
        return false;
      }
    }

    return true;
  }

  // Method to check if the form is valid considering conditional validation
  isFormValidWithConditionalValidation(): boolean {
    const baseFormValid = this.siddhaYogaParticipationForm.valid;
    const conditionalFieldsValid = this.validateConditionalFields();
    return baseFormValid && conditionalFieldsValid;
  }

  // Center Search API Methods - Refined and optimized
  getCenterList(searchTerm: string = '', listType: 'current' | 'other' = 'current') {
    // Require at least 3 characters for search
    if (searchTerm && searchTerm.length < 3) {
      return;
    }

    const reqBody = {
      "start": 0,
      "size": 100,
      "search": searchTerm || "",
      "sortfield": "",
      "sortdir": ""
    };

    this.api.apiCall('Center/SearchCenters', 'POST', reqBody).subscribe((response: any) => {
      if (response.Data && response.Data.Items && Array.isArray(response.Data.Items)) {
        const centerList = response.Data.Items.map((item: { Id: number, Name: string }) => ({
          name: item.Name.trim(),
          value: { Id: item.Id, Name: item.Name.trim() }
        }));

        if (listType === 'current') {
          this.currentCenterList = centerList;
        } else {
          this.otherCurrentCenterList = centerList;
        }
      } else {
        if (listType === 'current') {
          this.currentCenterList = [];
        } else {
          this.otherCurrentCenterList = [];
        }
      }
    }, (error) => {
      console.error('Error fetching centers:', error);
      if (listType === 'current') {
        this.currentCenterList = [];
      } else {
        this.otherCurrentCenterList = [];
      }
    });
  }

  // Handle search input for CurrentCenterDetails dropdown
  onCurrentCenterSearchInput(event: any) {
    const searchTerm = event.term || event;
    this.currentCenterSearchTerm = searchTerm;

    if (this.currentCenterSearchTimeout) {
      clearTimeout(this.currentCenterSearchTimeout);
    }

    if (searchTerm && searchTerm.length >= 3) {
      // Debounce: wait 300ms after user stops typing before calling API
      this.currentCenterSearchTimeout = setTimeout(() => {
        this.getCenterList(searchTerm, 'current');
      }, 300);
    } else if (!searchTerm || searchTerm.length === 0) {
      this.currentCenterSearchTerm = '';
      this.currentCenterList = [];
    }
  }

  // Handle search input for OtherCurrentCenterDetails dropdown
  onOtherCurrentCenterSearchInput(event: any) {
    const searchTerm = event.term || event;
    this.otherCurrentCenterSearchTerm = searchTerm;

    if (this.otherCurrentCenterSearchTimeout) {
      clearTimeout(this.otherCurrentCenterSearchTimeout);
    }

    if (searchTerm && searchTerm.length >= 3) {
      // Debounce: wait 300ms after user stops typing before calling API
      this.otherCurrentCenterSearchTimeout = setTimeout(() => {
        this.getCenterList(searchTerm, 'other');
      }, 300);
    } else if (!searchTerm || searchTerm.length === 0) {
      this.otherCurrentCenterSearchTerm = '';
      this.otherCurrentCenterList = [];
    }
  }

  // Unified search handler (for backward compatibility)
  onCenterSearchInput(event: any) {
    // Determine which dropdown is being used based on context
    // This will be handled by separate methods in template
    this.onCurrentCenterSearchInput(event);
  }

  onCurrentCenterClear() {
    this.currentCenterSearchTerm = '';
    this.currentCenterList = [];
  }

  // Debug event handlers for CurrentCenterDetails dropdown
  onCurrentCenterDropdownOpen() {
    console.log('🔴 [DEBUG] CurrentCenterDetails dropdown opened');
    this.debugCurrentCenterDetailsBinding();
  }

  onCurrentCenterChange(event: any) {
    console.log('🔴 [DEBUG] CurrentCenterDetails changed:', JSON.stringify(event, null, 2));
    const control = this.siddhaYogaParticipationForm.get('CurrentCenterDetails');
    console.log('🔴 [DEBUG] CurrentCenterDetails form control value after change:', JSON.stringify(control?.value, null, 2));
  }

  onOtherCurrentCenterClear() {
    this.otherCurrentCenterSearchTerm = '';
    this.otherCurrentCenterList = [];
  }

  // Unified clear handler (for backward compatibility)
  onCenterClear() {
    this.onCurrentCenterClear();
  }

  // Helper method to add center to appropriate list
  addCenterToList(centerObj: { Id: number, Name: string }, listType: 'current' | 'other') {
    console.log(`📝 [DEBUG] addCenterToList called - listType: ${listType}, centerObj:`, centerObj);
    const centerItem = {
      name: centerObj.Name.trim(),
      value: { Id: centerObj.Id, Name: centerObj.Name.trim() }
    };
    console.log(`📝 [DEBUG] centerItem created:`, centerItem);

    const targetList = listType === 'current' ? this.currentCenterList : this.otherCurrentCenterList;
    console.log(`📝 [DEBUG] targetList before add:`, targetList);
    
    // Check if center already exists in list
    const existingItem = targetList.find(c => c.value.Id === centerObj.Id);
    console.log(`📝 [DEBUG] existingItem found:`, existingItem);
    if (!existingItem) {
      targetList.push(centerItem);
      console.log(`📝 [DEBUG] centerItem added to list. targetList after add:`, targetList);
    } else {
      console.log(`📝 [DEBUG] centerItem already exists, not adding`);
    }
  }

  // Helper method to fetch center by ID and add to appropriate list
  fetchCenterById(centerId: number, centerName?: string, listType: 'current' | 'other' = 'current') {
    // If center name is provided, add it directly
    if (centerName) {
      this.addCenterToList({ Id: centerId, Name: centerName }, listType);
      return;
    }

    // If center name is not provided, fetch from API
    const reqBody = {
      "start": 0,
      "size": 100,
      "search": "",
      "sortfield": "",
      "sortdir": ""
    };

    this.api.apiCall('Center/SearchCenters', 'POST', reqBody).subscribe((response: any) => {
      if (response.Data && response.Data.Items && Array.isArray(response.Data.Items)) {
        const center = response.Data.Items.find((item: { Id: number }) => item.Id === centerId);
        if (center) {
          this.addCenterToList({ Id: center.Id, Name: center.Name }, listType);
        }
      }
    }, (error) => {
      console.error('Error fetching center by ID:', error);
    });
  }

  // Getters for FormArrays
  get retreatHistoryArray() {
    return this.siddhaYogaParticipationForm.get('retreatHistory') as FormArray;
  }

  get sevaHistoryArray() {
    return this.siddhaYogaParticipationForm.get('sevaHistory') as FormArray;
  }



  // Retreat History Methods
  addRetreatHistoryRow(retreatData?: RetreatHistoryItem) {
    // Check if all existing rows are filled before adding new one (only if not initial load)
    if (!retreatData && this.retreatHistoryArray.length > 0) {
      const allFieldsFilled = this.retreatHistoryArray.controls.every(control => {
        const formGroup = control as FormGroup;
        const retreatName = formGroup.get('RetreatName')?.value;
        const retreatDate = formGroup.get('RetreatDate')?.value;
        // RetreatName is always required, RetreatDate is required only if RetreatName is not "None"
        const isRetreatNameValid = retreatName && retreatName !== '';
        const isRetreatDateValid = !retreatName || retreatName === 'None' || retreatName === '' || retreatDate;
        return isRetreatNameValid && isRetreatDateValid;
      });

      if (!allFieldsFilled) {
        // Mark all empty fields as invalid and touched to show validation
        this.retreatHistoryArray.controls.forEach(control => {
          const formGroup = control as FormGroup;
          const retreatName = formGroup.get('RetreatName');
          const retreatDate = formGroup.get('RetreatDate');
          const retreatNameValue = retreatName?.value;

          if (!retreatNameValue || retreatNameValue === '') {
            retreatName?.markAsTouched();
            retreatName?.setErrors({ required: true });
          }
          // Only require RetreatDate if RetreatName is selected and not "None"
          if (retreatNameValue && retreatNameValue !== '' && retreatNameValue !== 'None' && !retreatDate?.value) {
            retreatDate?.markAsTouched();
            retreatDate?.setErrors({ required: true });
          }
        });

        this.submitted = true;
        this.toastr.warning(this.getTranslation('validation.fillAllFieldsBeforeAdding'));
        return;
      }
    }

    const retreatForm = this.fb.group({
      CompletionPercentage: [retreatData?.CompletionPercentage || 0],
      IsActive: [retreatData?.IsActive ?? true],
      Id: [retreatData?.Id || 0],
      SevaProfileDisplayUID: [retreatData?.SevaProfileDisplayUID || ''],
      RetreatName: [retreatData?.RetreatName || '',],
      RetreatDate: [retreatData?.RetreatDate || '',],
      SaveMode: [retreatData?.SaveMode || 0]
    });

    // Set up conditional validation: RetreatDate is required when RetreatName is not "None"
    this.setupRetreatDateConditionalValidation(retreatForm);

    this.retreatHistoryArray.push(retreatForm);
  }

  removeRetreatHistoryRow(index: number) {
    if (this.retreatHistoryArray.length > 1) {
      this.retreatHistoryArray.removeAt(index);
    } else {
      this.toastr.warning(this.getTranslation('validation.atLeastOneRetreatRequired'));
    }
  }

  populateRetreatHistoryArray(retreatHistory: RetreatHistoryItem[]) {
    // Clear existing form array
    while (this.retreatHistoryArray.length !== 0) {
      this.retreatHistoryArray.removeAt(0);
    }

    // Add rows for each retreat history item
    retreatHistory.forEach(retreat => {
      this.addRetreatHistoryRow(retreat);
    });
  }

  // Set up conditional validation for RetreatDate based on RetreatName
  setupRetreatDateConditionalValidation(retreatForm: FormGroup) {
    const retreatNameControl = retreatForm.get('RetreatName');
    const retreatDateControl = retreatForm.get('RetreatDate');

    if (retreatNameControl && retreatDateControl) {
      // Initial validation check
      this.updateRetreatDateValidation(retreatNameControl.value, retreatDateControl);

      // Listen for changes in RetreatName
      retreatNameControl.valueChanges.subscribe((retreatName: string) => {
        this.updateRetreatDateValidation(retreatName, retreatDateControl);
      });
    }
  }

  // Update RetreatDate validation based on RetreatName value
  updateRetreatDateValidation(retreatName: string, retreatDateControl: any) {
    if (retreatName && retreatName !== '' && retreatName !== 'None') {
      // RetreatName is selected and not "None", so RetreatDate is required
      retreatDateControl.setValidators([Validators.required]);
    } else {
      // RetreatName is empty or "None", so RetreatDate is not required
      retreatDateControl.clearValidators();
    }
    retreatDateControl.updateValueAndValidity();
  }

  // Seva History Methods
  addSevaHistoryRow(sevaData?: any) {
    // Check if all existing rows are filled before adding new one (only if not initial load)
    if (!sevaData && this.sevaHistoryArray.length > 0) {
      const allFieldsFilled = this.sevaHistoryArray.controls.every(control => {
        const formGroup = control as FormGroup;
        return formGroup.get('AshramName')?.value &&
          formGroup.get('SevaRole')?.value &&
          formGroup.get('Department')?.value &&
          formGroup.get('Year')?.value &&
          formGroup.get('Duration')?.value &&
          formGroup.get('SupervisorName')?.value;
      });

      if (!allFieldsFilled) {
        // Mark all empty fields as invalid and touched to show validation
        this.sevaHistoryArray.controls.forEach(control => {
          const formGroup = control as FormGroup;
          const ashramName = formGroup.get('AshramName');
          const sevaRole = formGroup.get('SevaRole');
          const department = formGroup.get('Department');
          const year = formGroup.get('Year');
          const duration = formGroup.get('Duration');
          const supervisorName = formGroup.get('SupervisorName');

          if (!ashramName?.value) {
            ashramName?.markAsTouched();
            ashramName?.setErrors({ required: true });
          }
          if (!sevaRole?.value) {
            sevaRole?.markAsTouched();
            sevaRole?.setErrors({ required: true });
          }
          if (!department?.value) {
            department?.markAsTouched();
            department?.setErrors({ required: true });
          }
          if (!year?.value) {
            year?.markAsTouched();
            year?.setErrors({ required: true });
          }
          if (!duration?.value) {
            duration?.markAsTouched();
            duration?.setErrors({ required: true });
          }
          if (!supervisorName?.value) {
            supervisorName?.markAsTouched();
            supervisorName?.setErrors({ required: true });
          }
        });

        this.submitted = true;
        this.toastr.warning(this.getTranslation('validation.fillAllFieldsBeforeAddingSeva'));
        return;
      }
    }

    const sevaForm = this.fb.group({
      CompletionPercentage: [sevaData?.CompletionPercentage || 0],
      IsActive: [sevaData?.IsActive ?? true],
      Id: [sevaData?.Id || 0],
      SevaProfileDisplayUID: [sevaData?.SevaProfileDisplayUID || ''],
      AshramName: [sevaData?.AshramName || '',],
      SevaRole: [sevaData?.SevaRole || '',],
      Department: [sevaData?.Department || '',],
      Year: [sevaData?.Year || '',],
      Duration: [sevaData?.Duration || ''],
      SupervisorName: [sevaData?.SupervisorName || '',],
      SaveMode: [sevaData?.SaveMode || 0]
    });

    this.sevaHistoryArray.push(sevaForm);
  }

  removeSevaHistoryRow(index: number) {
    if (this.sevaHistoryArray.length > 1) {
      this.sevaHistoryArray.removeAt(index);
    } else {
      this.toastr.warning(this.getTranslation('validation.atLeastOneSevaRequired'));
    }
  }

  populateSevaHistoryArray(sevaHistory: any[]) {
    // Clear existing form array
    while (this.sevaHistoryArray.length !== 0) {
      this.sevaHistoryArray.removeAt(0);
    }
    // Add rows for each seva history item
    sevaHistory.forEach(seva => {
      this.addSevaHistoryRow(seva);
    });
  }

  // Validation helper methods
  validateFormArrays(): boolean {
    let isValid = true;

    // Validate Retreat History
    this.retreatHistoryArray.controls.forEach((control, index) => {
      if (control.invalid) {
        control.markAsTouched();
        isValid = false;
      }
    });

    // Validate Seva History
    this.sevaHistoryArray.controls.forEach((control, index) => {
      if (control.invalid) {
        control.markAsTouched();
        isValid = false;
      }
    });

    return isValid;
  }

  // Check if all required fields in a row are filled
  isRowComplete(formGroup: any): boolean {
    if (formGroup instanceof FormGroup) {
      const controls = formGroup.controls;
      return Object.keys(controls).every(key => {
        const control = controls[key];
        if (key === 'CompletionPercentage' || key === 'IsActive' || key === 'Id' || key === 'SevaProfileDisplayUID' || key === 'SaveMode') {
          return true; // These are not user input fields
        }
        return control.value && control.value.toString().trim() !== '';
      });
    }
    return false;
  }


  onSubmit(Mode: number): void {
  

    if (!this.isFormValidWithConditionalValidation() && Mode == this.SaveMode.SAVE) {
      this.submitted = true;

      // Add 'submitted' class manually to all invalid controls
      Object.keys(this.siddhaYogaParticipationForm.controls).forEach((field) => {
        const control = this.siddhaYogaParticipationForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched(); // Ensure ng-touched is triggered

          const element = document.getElementById(field);
          if (element) {
            element.classList.add('submitted'); // Add 'submitted' class to invalid elements
          }
        }
      });

      // Validate conditional fields
      if (!this.validateConditionalFields()) {
        this.toastr.warning(this.getTranslation('validation.completeAllFields'));

        return;
      }

      // Validate FormArrays
      if (!this.validateFormArrays()) {
        return;
      }
      this.toastr.warning(this.getTranslation('validation.completeAllFields'));

      return
    }

    const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.siddhaYogaParticipationForm.value);

    // 👇 Convert array controls to JSON string (backend expects string fields as per swagger)
    const arrayFields = [
      'IntensiveHistory',
      'AudioSatsangHistory',
      'LiveStreamHistory',
      'MDSHistory',
      'SweetSurpriseHistory',
      'EngagedTeachingDetails',
      'CurrentSYPractices'
    ];

    arrayFields.forEach(field => {
      if (cleanedValues[field] && Array.isArray(cleanedValues[field])) {
        cleanedValues[field] = JSON.stringify(cleanedValues[field]);
      }
    });

    // 🔹 Single year fields → ensure string
    const yearFields = ['BeginPracticeYear', 'FirstIntensiveYear'];
    yearFields.forEach(field => {
      if (cleanedValues[field] !== null && cleanedValues[field] !== undefined) {
        cleanedValues[field] = String(cleanedValues[field]);
      }
    });

    // 🔹 Convert OtherCurrentCenterDetails based on PracticeBeginDetails value
    const practiceBeginDetails = cleanedValues.PracticeBeginDetails;
    
    if (practiceBeginDetails === 'other') {
      // For "other" option, OtherCurrentCenterDetails is a text string
      if (cleanedValues.OtherCurrentCenterDetails && typeof cleanedValues.OtherCurrentCenterDetails === 'string') {
        cleanedValues.OtherPracticeBeginDetails = cleanedValues.OtherCurrentCenterDetails.trim();
      }
      delete cleanedValues.OtherCurrentCenterDetails;
      delete cleanedValues.OtherCurrentCenterDetailsObj;
    } else if (practiceBeginDetails === 'siddha-yoga-centre') {
      // For "siddha-yoga-centre" option, OtherCurrentCenterDetails is an object from dropdown
      let centerObj: { Id: number, Name: string } | null = null;
      
      if (cleanedValues.OtherCurrentCenterDetails) {
        // Handle object format from ng-select
        if (typeof cleanedValues.OtherCurrentCenterDetails === 'object' && cleanedValues.OtherCurrentCenterDetails.Id) {
          centerObj = {
            Id: cleanedValues.OtherCurrentCenterDetails.Id,
            Name: cleanedValues.OtherCurrentCenterDetails.Name || ''
          };
        } else if (typeof cleanedValues.OtherCurrentCenterDetails === 'number') {
          // Fallback: find center by ID from list
          const selectedCenter = this.otherCurrentCenterList.find(c => c.value.Id === cleanedValues.OtherCurrentCenterDetails);
          if (selectedCenter) {
            centerObj = { Id: selectedCenter.value.Id, Name: selectedCenter.value.Name };
          }
        }

        if (centerObj) {
          cleanedValues.OtherCurrentCenterDetailsObj = centerObj;
        }
      }
      delete cleanedValues.OtherCurrentCenterDetails;
    } else {
      // Neither option selected, remove the field
      delete cleanedValues.OtherCurrentCenterDetails;
      delete cleanedValues.OtherCurrentCenterDetailsObj;
    }

    // 🔹 Convert CurrentCenterDetails to object format for API submission
    if (cleanedValues.CurrentCenterDetails !== null && cleanedValues.CurrentCenterDetails !== undefined) {
      let centerObj: { Id: number, Name: string } | null = null;
      
      // Handle object format from ng-select
      if (typeof cleanedValues.CurrentCenterDetails === 'object' && cleanedValues.CurrentCenterDetails.Id) {
        centerObj = {
          Id: cleanedValues.CurrentCenterDetails.Id,
          Name: cleanedValues.CurrentCenterDetails.Name || ''
        };
      } else if (typeof cleanedValues.CurrentCenterDetails === 'number') {
        // Fallback: find center by ID from list
        const selectedCenter = this.currentCenterList.find(c => c.value.Id === cleanedValues.CurrentCenterDetails);
        if (selectedCenter) {
          centerObj = { Id: selectedCenter.value.Id, Name: selectedCenter.value.Name };
        }
      }

      if (centerObj) {
        cleanedValues.CurrentCenterDetailsObj = centerObj;
        delete cleanedValues.CurrentCenterDetails;
      }
    }

    // 🔹 Ensure Seva History Year is a string for each entry
    if (Array.isArray(cleanedValues.sevaHistory)) {
      cleanedValues.sevaHistory = cleanedValues.sevaHistory.map((seva: any) => ({
        ...seva,
        Year: seva?.Year !== null && seva?.Year !== undefined ? String(seva.Year) : seva?.Year
      }));
    }

    this.addUpdate(cleanedValues, Mode);
  }


  addUpdate(reqBody: any, Mode: number) {

    reqBody.SaveMode = Mode;
    // Prepare data for API - separate main form data from FormArrays
    const mainFormData = { ...reqBody };
    
    // Remove FormArrays from main form data (they're handled separately if needed)
    delete mainFormData.retreatHistory;
    delete mainFormData.sevaHistory;

    // Map UI-specific field to backend field name as per swagger / C# entity
    if (mainFormData.SiddhaYogaPathWebsiteFrequency !== undefined) {
      mainFormData.WebSiteSearchFrequency = mainFormData.SiddhaYogaPathWebsiteFrequency;
      delete mainFormData.SiddhaYogaPathWebsiteFrequency;
    }

    // Ensure CurrentCenterDetailsObj and OtherCurrentCenterDetailsObj are properly formatted
    // (Already handled in onSubmit method, but double-check here)
    if (mainFormData.CurrentCenterDetailsObj && typeof mainFormData.CurrentCenterDetailsObj === 'object') {
      // Ensure it has the correct structure
      if (!mainFormData.CurrentCenterDetailsObj.Id || !mainFormData.CurrentCenterDetailsObj.Name) {
        delete mainFormData.CurrentCenterDetailsObj;
      }
    }

    if (mainFormData.OtherCurrentCenterDetailsObj && typeof mainFormData.OtherCurrentCenterDetailsObj === 'object') {
      // Ensure it has the correct structure
      if (!mainFormData.OtherCurrentCenterDetailsObj.Id || !mainFormData.OtherCurrentCenterDetailsObj.Name) {
        delete mainFormData.OtherCurrentCenterDetailsObj;
      }
    }

    // Save main form data
    this.api.apiCall('CurrentParticipation/SaveCurrentParticipation', 'POST', mainFormData).subscribe((response: any) => {
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else if (Mode == this.SaveMode.DRAFT) {
        this.getApplicantData();
      }
    }, (error) => {
      console.error('Error saving current participation:', error);
      this.toastr.error(this.getTranslation('validation.saveError') || 'Error saving data');
    });
  }


  goToBack(): void {
    this.previous.emit();
  }

  getPracticeYearControl() {
    return this.siddhaYogaParticipationForm.get('BeginPracticeYear') as any;
  }

  getFirstIntensiveYearControl() {
    return this.siddhaYogaParticipationForm.get('FirstIntensiveYear') as any;
  }

  isSiddhaYogaPathWebsiteSelected(): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('EngagedTeachingDetails')?.value || [];
    return selectedValues.includes('Siddha Yoga Path Website');
  }

  isOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('EngagedTeachingDetails')?.value || [];
    return selectedValues.includes(value);
  }

  toggleOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('EngagedTeachingDetails');
    const currentValues = control?.value || [];

    if (this.isOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // Add the value if not selected
      const newValues = [...currentValues, value];
      control?.setValue(newValues);
    }
  }



  // Change event handlers for debugging
  onEngagedTeachingDetailsChange(event: any): void {
    // console.log('EngagedTeachingDetails change event:', event);
    // console.log('Form control value:', this.siddhaYogaParticipationForm.get('EngagedTeachingDetails')?.value);
  }

  // Set up CurrentSYPractices change listener for mutual exclusivity
  setupCurrentSYPracticesListener() {
    const control = this.siddhaYogaParticipationForm.get('CurrentSYPractices');
    if (!control) {
      return;
    }

    let previousValue: string[] = [];

    control.valueChanges.subscribe((selectedValues: string[]) => {
      if (this.isBootstrapping) {
        previousValue = selectedValues || [];
        return;
      }

      if (!selectedValues || !Array.isArray(selectedValues)) {
        previousValue = [];
        return;
      }

      const hasNone = selectedValues.includes('None');
      const otherOptions = selectedValues.filter(v => v !== 'None');
      const previousHasNone = previousValue.includes('None');

      // If "None" is selected along with other options
      if (hasNone && otherOptions.length > 0) {
        // If "None" was just added (wasn't in previous), keep only "None"
        if (!previousHasNone) {
          control.setValue(['None'], { emitEvent: false });
          previousValue = ['None'];
          return;
        }
        // If another option was just added (None was already selected), remove "None"
        else {
          control.setValue(otherOptions, { emitEvent: false });
          previousValue = otherOptions;
          return;
        }
      }
      // If "None" is not in selection but was in previous, it was removed - that's fine
      // If other options are selected without "None", that's fine too

      previousValue = selectedValues;
    });
  }

  // Handle CurrentSYPractices change - ensure "None" and other options are mutually exclusive
  onCurrentSYPracticesChange(selectedValues: string[]): void {
    // This method is called by the template change event
    // The actual logic is handled in setupCurrentSYPracticesListener via valueChanges
  }

  onIntensiveHistoryChange(event: any): void {
    // console.log('Form control value:', this.siddhaYogaParticipationForm.get('IntensiveHistory')?.value);
    this.monitorFormControl('IntensiveHistory');
  }

  onAudioSatsangHistoryChange(event: any): void {
    // console.log('Form control value:', this.siddhaYogaParticipationForm.get('AudioSatsangHistory')?.value);
    this.monitorFormControl('AudioSatsangHistory');
  }

  onLiveStreamHistoryChange(event: any): void {
    this.monitorFormControl('LiveStreamHistory');
  }

  onMDSHistoryChange(event: any): void {
    this.monitorFormControl('MDSHistory');
  }

  onSweetSurpriseHistoryChange(event: any): void {
    this.monitorFormControl('SweetSurpriseHistory');
  }

  // Monitor form control changes
  monitorFormControl(controlName: string): void {
    const control = this.siddhaYogaParticipationForm.get(controlName);
    if (control) {
      control.valueChanges.subscribe(value => {
      });
    }
  }

  // Add missing methods for engaged teaching options
  isEngagedTeachingOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('EngagedTeachingDetails')?.value || [];
    return selectedValues.includes(value);
  }

  toggleEngagedTeachingOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('EngagedTeachingDetails');
    const currentValues = control?.value || [];

    if (this.isEngagedTeachingOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // Add the value if not selected
      const newValues = [...currentValues, value];
      control?.setValue(newValues);
    }
  }

  // Add missing methods for intensive history options
  isIntensiveHistoryOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('IntensiveHistory')?.value || [];
    return selectedValues.includes(value);
  }

  toggleIntensiveHistoryOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('IntensiveHistory');
    const currentValues = control?.value || [];

    if (this.isIntensiveHistoryOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // If "None" is being selected, clear all other options
      if (value === 'None') {
        control?.setValue(['None']);
      } else {
        // If a year is being selected and "None" is currently selected, remove "None"
        const hasNone = currentValues.includes('None');
        const newValues = hasNone ? [value] : [...currentValues, value];
        control?.setValue(newValues);
      }
    }
  }

  // Add missing methods for audio satsang history options
  isAudioSatsangHistoryOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('AudioSatsangHistory')?.value || [];
    return selectedValues.includes(value);
  }

  toggleAudioSatsangHistoryOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('AudioSatsangHistory');
    const currentValues = control?.value || [];

    if (this.isAudioSatsangHistoryOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // If "None" is being selected, clear all other options
      if (value === 'None') {
        control?.setValue(['None']);
      } else {
        // If a year is being selected and "None" is currently selected, remove "None"
        const hasNone = currentValues.includes('None');
        const newValues = hasNone ? [value] : [...currentValues, value];
        control?.setValue(newValues);
      }
    }
  }

  // Add missing methods for live stream history options
  isLiveStreamHistoryOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('LiveStreamHistory')?.value || [];
    return selectedValues.includes(value);
  }

  toggleLiveStreamHistoryOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('LiveStreamHistory');
    const currentValues = control?.value || [];

    if (this.isLiveStreamHistoryOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // If "None" is being selected, clear all other options
      if (value === 'None') {
        control?.setValue(['None']);
      } else {
        // If a year is being selected and "None" is currently selected, remove "None"
        const hasNone = currentValues.includes('None');
        const newValues = hasNone ? [value] : [...currentValues, value];
        control?.setValue(newValues);
      }
    }
  }

  // Add missing methods for MDS history options
  isMDSHistoryOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('MDSHistory')?.value || [];
    return selectedValues.includes(value);
  }

  toggleMDSHistoryOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('MDSHistory');
    const currentValues = control?.value || [];

    if (this.isMDSHistoryOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // If "None" is being selected, clear all other options
      if (value === 'None') {
        control?.setValue(['None']);
      } else {
        // If a year is being selected and "None" is currently selected, remove "None"
        const hasNone = currentValues.includes('None');
        const newValues = hasNone ? [value] : [...currentValues, value];
        control?.setValue(newValues);
      }
    }
  }

  // Add missing methods for sweet surprise history options
  isSweetSurpriseHistoryOptionSelected(value: string): boolean {
    const selectedValues = this.siddhaYogaParticipationForm.get('SweetSurpriseHistory')?.value || [];
    return selectedValues.includes(value);
  }

  toggleSweetSurpriseHistoryOption(value: string): void {
    const control = this.siddhaYogaParticipationForm.get('SweetSurpriseHistory');
    const currentValues = control?.value || [];

    if (this.isSweetSurpriseHistoryOptionSelected(value)) {
      // Remove the value if already selected
      const newValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(newValues);
    } else {
      // If "None" is being selected, clear all other options
      if (value === 'None') {
        control?.setValue(['None']);
      } else {
        // If a year is being selected and "None" is currently selected, remove "None"
        const hasNone = currentValues.includes('None');
        const newValues = hasNone ? [value] : [...currentValues, value];
        control?.setValue(newValues);
      }
    }
  }

  // Set up listeners for event history fields to handle "None" option mutual exclusivity
  setupEventHistoryListeners(): void {
    const fields = ['IntensiveHistory', 'AudioSatsangHistory', 'LiveStreamHistory', 'MDSHistory', 'SweetSurpriseHistory'];
    
    fields.forEach(fieldName => {
      const control = this.siddhaYogaParticipationForm.get(fieldName);
      if (!control) {
        return;
      }

      let previousValue: string[] = [];

      control.valueChanges.subscribe((selectedValues: string[]) => {
        if (this.isBootstrapping) {
          previousValue = selectedValues || [];
          return;
        }

        if (!selectedValues || !Array.isArray(selectedValues)) {
          previousValue = [];
          return;
        }

        const hasNone = selectedValues.includes('None');
        const otherOptions = selectedValues.filter(v => v !== 'None');
        const previousHasNone = previousValue.includes('None');

        // If "None" is selected along with other options
        if (hasNone && otherOptions.length > 0) {
          // If "None" was just added (wasn't in previous), keep only "None"
          if (!previousHasNone) {
            control.setValue(['None'], { emitEvent: false });
            previousValue = ['None'];
            return;
          }
          // If another option was just added (None was already selected), remove "None"
          else {
            control.setValue(otherOptions, { emitEvent: false });
            previousValue = otherOptions;
            return;
          }
        }

        previousValue = selectedValues;
      });
    });
  }


  last10Years = this.getLastNYears(10);
  last10YearsWithNone = [{ name: 'None', value: 'None' }, ...this.last10Years];


  getLastNYears(n: number): { name: string, value: string }[] {
    const currentYear = new Date().getFullYear();
    const years: { name: string, value: string }[] = [];

    for (let i = 0; i < n; i++) {
      const year = (currentYear - i).toString();
      years.push({ name: year, value: year });
    }

    return years;
  }

}